<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import {
  extractCrmPersonInfo,
  applyCrmPersonExtraction,
} from 'dashboard/api/crmPersonExtraction';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['update:show']);

const { t } = useI18n();
const { accountId } = useAccount();

// [xleaders] `loading` -> gọi /extract xong -> `ready` (duyệt & tick) -> `saving` -> `done`
// (tóm tắt field nào ghi được, field nào không). `error` là lỗi ở bước /extract.
const PHASE = {
  LOADING: 'loading',
  READY: 'ready',
  SAVING: 'saving',
  DONE: 'done',
  ERROR: 'error',
};

const phase = ref(PHASE.LOADING);
const proposals = ref([]);
// Object thường (không phải Set) vì ref() tự làm reactive object lồng bên trong,
// và template có thể v-model thẳng vào selected[field] mà không cần .value.
const selected = ref({});
const errorMessage = ref('');
const showPersonNotLinkedHint = ref(false);
const writtenLabels = ref([]);
const skippedLabels = ref([]);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const hasSelection = computed(() =>
  Object.values(selected.value).some(Boolean)
);

// keNhiem là field BOOLEAN duy nhất trong whitelist 10 field và là field duy nhất
// không có value_label từ backend — không dịch ở đây thì modal hiện nguyên "true"/"false".
const valueText = (value, label) => {
  if (label) return label;
  if (value === true) {
    return t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.VALUE.YES');
  }
  if (value === false) {
    return t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.VALUE.NO');
  }
  return value;
};

const resetState = () => {
  phase.value = PHASE.LOADING;
  proposals.value = [];
  selected.value = {};
  errorMessage.value = '';
  showPersonNotLinkedHint.value = false;
  writtenLabels.value = [];
  skippedLabels.value = [];
};

const setErrorFromResponse = error => {
  errorMessage.value =
    error?.response?.data?.error ||
    t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.GENERIC_ERROR');
  // 422 = hội thoại chưa nối Person trong CRM, ca thường gặp nhất và salesperson tự sửa được
  // ngay từ panel sidebar — đáng để chỉ rõ đường đi thay vì chỉ lặp lại thông điệp lỗi.
  showPersonNotLinkedHint.value = error?.response?.status === 422;
};

const runExtraction = async () => {
  phase.value = PHASE.LOADING;
  try {
    const { data } = await extractCrmPersonInfo(
      accountId.value,
      props.conversationId
    );
    proposals.value = data.proposals || [];
    const initialSelection = {};
    // Ca ghi đè (mode 'change') KHÔNG tick sẵn: tick sẵn biến màn duyệt thành hình thức,
    // và ca người nhà/trợ lý nhắn hộ khách sẽ lọt. Ca điền vào chỗ trống (mode 'fill') thì
    // tick sẵn vì không phá gì cả.
    proposals.value.forEach(item => {
      initialSelection[item.field] = item.mode === 'fill';
    });
    selected.value = initialSelection;
    phase.value = PHASE.READY;
  } catch (error) {
    setErrorFromResponse(error);
    phase.value = PHASE.ERROR;
  }
};

watch(
  () => props.show,
  show => {
    if (!show) return;
    resetState();
    runExtraction();
  }
);

const save = async () => {
  // Không đọc giá trị đã tick từ DOM: input[type=checkbox] không mang giá trị field, còn nếu
  // đọc qua input[type=text]/value thì mọi giá trị bị ép thành String — field BOOLEAN keNhiem sẽ
  // hoá "true"/"false" dạng chuỗi. Giá trị đúng kiểu (bao gồm boolean thật) vẫn còn nguyên trong
  // mảng `proposals`, tra lại theo field name là đủ.
  const fields = {};
  const labelsByField = {};
  proposals.value.forEach(item => {
    if (selected.value[item.field]) {
      fields[item.field] = item.value;
      labelsByField[item.field] = item.label || item.field;
    }
  });
  if (!Object.keys(fields).length) return;

  phase.value = PHASE.SAVING;
  try {
    const { data } = await applyCrmPersonExtraction(
      accountId.value,
      props.conversationId,
      fields
    );
    const written = data.written_fields || [];
    // Server có quyền từ chối một field ĐÃ TICK (thường vì CRM vừa có giá trị cho field
    // fill_only đó). Bỏ qua written_fields ở đây coi như "thành công giả" trên dữ liệu liên lạc
    // của khách — phải đối chiếu và báo rõ field nào không ghi được.
    writtenLabels.value = Object.keys(fields)
      .filter(field => written.includes(field))
      .map(field => labelsByField[field]);
    skippedLabels.value = Object.keys(fields)
      .filter(field => !written.includes(field))
      .map(field => labelsByField[field]);
    phase.value = PHASE.DONE;
  } catch (error) {
    setErrorFromResponse(error);
    phase.value = PHASE.ERROR;
  }
};

const close = () => {
  localShow.value = false;
};

// ReplyBox không bị remount khi đổi hội thoại (không có :key theo conversationId), nên modal này
// vẫn sống nguyên qua một lần chuyển hội thoại nếu không tự đóng. Không đóng thì `proposals` đang
// hiện trên màn hình vẫn là của hội thoại CŨ trong khi `save()` đọc `props.conversationId` MỚI —
// bấm "Ghi sang CRM" sẽ ghi dữ liệu trích từ khách A sang Person của khách B. Đổi conversationId
// trong lúc đang mở phải đóng modal ngay, bắt sale mở lại (và trích xuất lại) cho đúng hội thoại.
//
// TRỪ lúc đang SAVING: request PATCH đã bay đi với đúng conversationId cũ (tham số được chốt giá
// trị lúc gọi, không đọc lại reactive), nên bản thân việc ghi vẫn đúng chỗ — không có rủi ro ghi
// nhầm hội thoại. Nhưng đóng modal ngay lúc này làm mất luôn cảnh báo "field nào chưa ghi được":
// `close()` chỉ ẩn modal (component không bị unmount, `woot-modal` chỉ toggle `v-if` bên trong),
// nên `.then` của `save()` vẫn chạy và đổ writtenLabels/skippedLabels vào một modal đã ẩn — lần mở
// tiếp theo `resetState()` xoá sạch trước khi sale kịp thấy. Để save() chạy xong, hiện kết quả, rồi
// mới đóng ở lần đổi hội thoại KẾ TIẾP.
watch(
  () => props.conversationId,
  () => {
    if (props.show && phase.value !== PHASE.SAVING) close();
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="close" size="modal-big">
    <woot-modal-header
      :header-title="
        $t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.TITLE')
      "
      :header-content="
        $t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.SUBTITLE')
      "
    />

    <div class="flex flex-col gap-4 px-8 pb-8">
      <div
        v-if="phase === PHASE.LOADING"
        class="flex flex-col items-center gap-2 py-8"
      >
        <Spinner />
        <p class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.LOADING') }}
        </p>
      </div>

      <div v-else-if="phase === PHASE.ERROR" class="flex flex-col gap-3">
        <Banner color="ruby">{{ errorMessage }}</Banner>
        <Banner v-if="showPersonNotLinkedHint" color="amber">
          {{
            $t(
              'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.PERSON_NOT_LINKED_HINT'
            )
          }}
        </Banner>
        <div class="flex justify-end gap-2">
          <Button
            color="slate"
            variant="faded"
            :label="
              $t(
                'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.BUTTONS.CLOSE'
              )
            "
            @click="close"
          />
          <Button
            :label="
              $t(
                'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.BUTTONS.RETRY'
              )
            "
            @click="runExtraction"
          />
        </div>
      </div>

      <div v-else-if="phase === PHASE.DONE" class="flex flex-col gap-3">
        <Banner v-if="writtenLabels.length" color="teal">
          {{
            $t(
              'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.WRITTEN_FIELDS',
              { fields: writtenLabels.join(', ') }
            )
          }}
        </Banner>
        <Banner v-if="skippedLabels.length" color="amber">
          {{
            $t(
              'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.SKIPPED_FIELDS',
              { fields: skippedLabels.join(', ') }
            )
          }}
        </Banner>
        <div class="flex justify-end">
          <Button
            :label="
              $t(
                'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.BUTTONS.CLOSE'
              )
            "
            @click="close"
          />
        </div>
      </div>

      <div v-else class="flex flex-col gap-3">
        <p
          v-if="!proposals.length"
          class="text-sm text-n-slate-11 text-center py-4"
        >
          {{ $t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.EMPTY') }}
        </p>

        <div v-else class="flex flex-col gap-3 max-h-[24rem] overflow-y-auto">
          <label
            v-for="item in proposals"
            :key="item.field"
            class="flex flex-col gap-1 pb-3 border-b border-n-weak last:border-b-0 cursor-pointer"
          >
            <span class="flex items-start gap-2">
              <input
                v-model="selected[item.field]"
                type="checkbox"
                class="mt-0.5"
                :disabled="phase === PHASE.SAVING"
              />
              <span class="text-sm text-n-slate-12">
                <I18nT
                  keypath="INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.ROW_VALUE"
                  tag="span"
                >
                  <template #label>
                    <strong>{{ item.label }}</strong>
                  </template>
                  <template #value>
                    {{ valueText(item.value, item.value_label) }}
                  </template>
                </I18nT>
                <span v-if="item.mode === 'change'" class="text-n-amber-11">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.CURRENT_VALUE',
                      { value: valueText(item.current, item.current_label) }
                    )
                  }}
                </span>
              </span>
            </span>
            <span class="text-xs italic text-n-slate-11 ml-6">
              {{
                $t('INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.QUOTE', {
                  quote: item.quote,
                })
              }}
            </span>
          </label>
        </div>

        <div v-if="proposals.length" class="flex justify-end gap-2">
          <Button
            color="slate"
            variant="faded"
            :disabled="phase === PHASE.SAVING"
            :label="
              $t(
                'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.BUTTONS.CANCEL'
              )
            "
            @click="close"
          />
          <Button
            :is-loading="phase === PHASE.SAVING"
            :disabled="!hasSelection || phase === PHASE.SAVING"
            :label="
              phase === PHASE.SAVING
                ? $t(
                    'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.BUTTONS.SAVING'
                  )
                : $t(
                    'INTEGRATION_SETTINGS.OPEN_AI.CRM_EXTRACTION_MODAL.BUTTONS.SAVE'
                  )
            "
            @click="save"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
